import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  ShieldCheck,
  Calendar,
  ArrowSquareOut,
  CheckCircle,
  Clock,
  Star,
  Phone,
  FolderOpen,
  Warning,
  Info,
  Buildings,
  CalendarBlank,
  CalendarCheck,
  Circle,
  ArrowClockwise,
  CaretRight,
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import QuarterlyMonitoring from '../components/QuarterlyMonitoring';
import { toast } from 'sonner';
import API_URL from '../config';

const PROTECTION_PLAN_CALL_BOOKING_URL = 'https://meetings-na2.hubspot.com/joey-vitale/protection-plan-call';

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const countdownLabel = (days) => {
  if (days < 0) return 'OVERDUE';
  if (days === 0) return 'today';
  if (days < 30) return `in ${days} day${days === 1 ? '' : 's'}`;
  if (days < 365) {
    const m = Math.round(days / 30);
    return `in ${m} month${m === 1 ? '' : 's'}`;
  }
  const y = (days / 365).toFixed(1).replace(/\.0$/, '');
  return `in ${y} year${y === '1' ? '' : 's'}`;
};

// Re-bucket urgency for maintenance using actionable_date when present
const maintenanceUrgency = (d) => {
  if (d.urgency) return d.urgency;
  const days = d.days_to_actionable ?? d.days_remaining ?? 9999;
  if (days < 0) return 'overdue';
  if (days <= 90) return 'urgent';
  if (days <= 365) return 'soon';
  return 'normal';
};

const urgencyStyles = {
  overdue: { chip: 'bg-red-100 text-red-700 border border-red-200', card: 'border-l-4 border-l-red-500' },
  urgent:  { chip: 'bg-red-100 text-red-700 border border-red-200', card: 'border-l-4 border-l-red-500' },
  soon:    { chip: 'bg-amber-100 text-amber-700 border border-amber-200', card: 'border-l-4 border-l-amber-500' },
  normal:  { chip: 'bg-emerald-100 text-emerald-700 border border-emerald-200', card: 'border-l-4 border-l-emerald-500' },
};

// Date-first headline for a maintenance deadline, e.g.
// "Window opens May 29, 2028 · §8 Declaration of Use".
const deadlineHeadline = (d) => {
  const stage = d.stage || 'upcoming';
  const phrase =
    stage === 'open'    ? `Due ${fmtDate(d.deadline_closes)}` :
    stage === 'grace'   ? `Grace ends ${fmtDate(d.grace_ends)}` :
    stage === 'overdue' ? `Past grace — ${fmtDate(d.grace_ends)}` :
                          `Window opens ${fmtDate(d.window_opens)}`;
  return d.label ? `${phrase} · ${d.label}` : phrase;
};

// Relative hint only when under 12 months away ("in 5 months"); nothing otherwise.
const deadlineRelHint = (days) => {
  if (days === null || days === undefined) return '';
  if (days < 0 || days >= 365) return '';
  return countdownLabel(days);
};

// -----------------------------------------------------------------------------
// Plan at a glance (merged status strip)
// -----------------------------------------------------------------------------
function PlanGlanceStrip({ data }) {
  const { registered_count = 0, next_deadline, last_monitored_at, brand_watch } = data;
  const every = brand_watch?.every || 'every quarter';
  const adj = brand_watch?.adjective || 'quarterly';
  const adjTitle = adj.charAt(0).toUpperCase() + adj.slice(1);
  const nextUpdateLabel = brand_watch?.next_update_label || '—';

  const cell = (testid, label, value, sub, subClass = 'text-[#52525B]') => (
    <div className="bg-white/70 border border-[#22C55E]/15 p-4" data-testid={testid}>
      <p className="label-uppercase text-[#1d57a5] text-xs font-semibold">{label}</p>
      <p className="text-xl font-bold text-[#141b4d] mt-1 leading-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
        {value}
      </p>
      {sub && <p className={`text-xs mt-0.5 ${subClass}`}>{sub}</p>}
    </div>
  );

  return (
    <div data-testid="pp-glance-strip">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-[#22C55E]/10 flex items-center justify-center flex-shrink-0">
          <ShieldCheck weight="duotone" className="w-6 h-6 text-[#22C55E]" />
        </div>
        <h2 className="text-lg font-semibold text-[#09090B]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Your plan at a glance
        </h2>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cell('pp-kpi-registered', 'Marks Covered', registered_count,
          registered_count === 1 ? 'registered trademark' : 'registered trademarks')}

        {/* Next maintenance deadline — date + what it is, registered mark + Reg. No. */}
        <div className="bg-white/70 border border-[#22C55E]/15 p-4" data-testid="pp-kpi-next-deadline">
          <p className="label-uppercase text-[#1d57a5] text-xs font-semibold">Next Maintenance Deadline</p>
          {next_deadline ? (() => {
            const hint = deadlineRelHint(next_deadline.days_to_actionable);
            const urgent = next_deadline.days_to_actionable !== undefined && next_deadline.days_to_actionable <= 90;
            return (
              <>
                <p className="text-base font-bold text-[#141b4d] mt-1 leading-snug" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  {deadlineHeadline(next_deadline)}
                </p>
                <p className="text-xs text-[#52525B] mt-1">
                  {next_deadline.mark_text || next_deadline.trademark_name}
                  {next_deadline.registration_number ? ` · Reg. ${next_deadline.registration_number}` : ''}
                </p>
                {hint && <p className={`text-xs mt-0.5 ${urgent ? 'text-red-700' : 'text-[#71717A]'}`}>{hint}</p>}
              </>
            );
          })() : (
            <>
              <p className="text-xl font-bold text-[#141b4d] mt-1" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>None upcoming</p>
              <p className="text-xs text-[#52525B] mt-0.5">You're all set.</p>
            </>
          )}
        </div>

        {cell('pp-kpi-last-monitored', 'Last USPTO Status Check',
          last_monitored_at ? fmtDate(last_monitored_at) : '—',
          last_monitored_at ? 'Auto-checks twice daily' : 'Awaiting first check')}
        {cell('pp-kpi-next-update', `Next ${adjTitle} Update`, nextUpdateLabel, 'Brand Watch review')}
      </div>

      <p className="text-sm text-[#52525B] mt-3" data-testid="pp-glance-subline">
        We check your registrations with the USPTO twice daily, and review new filings that resemble your marks {every}.
      </p>
    </div>
  );
}

// -----------------------------------------------------------------------------
// VIP Strategy Call activity (usage history + next availability)
// -----------------------------------------------------------------------------
function VipCallSection({ initial }) {
  const [vip, setVip] = useState(initial || null);
  const [refreshing, setRefreshing] = useState(false);

  if (!vip) return null;

  const {
    booking_url,
    quarter_label,
    used_this_quarter,
    next_available,
    last_completed_at,
    next_scheduled_at,
    history = [],
    synced_at,
  } = vip;

  const refresh = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API_URL}/api/client/protection-plan/refresh-calls`);
      setVip(res.data.vip_call);
      toast.success('Plan activity refreshed');
    } catch (e) {
      toast.error("Couldn't refresh plan activity");
    } finally {
      setRefreshing(false);
    }
  };

  const isAvailable = !used_this_quarter;

  return (
    <div data-testid="pp-vip-call-section">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h3 className="text-lg font-semibold text-[#09090B]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Your VIP Strategy Call
        </h3>
        <div className="flex items-center gap-3">
          {synced_at && (
            <span className="text-xs text-[#A1A1AA] hidden sm:inline" data-testid="pp-vip-synced-at">
              Updated {fmtDate(synced_at)}
            </span>
          )}
          <button
            onClick={refresh}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1d57a5] hover:text-[#141b4d] disabled:opacity-50 transition-colors"
            data-testid="pp-vip-refresh-btn"
          >
            <ArrowClockwise weight="bold" className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div
        className={`card-swiss ${isAvailable ? 'border-l-4 border-l-[#22C55E]' : 'border-l-4 border-l-[#141b4d]'}`}
        data-testid="pp-vip-status-card"
      >
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5">
          <div className="flex items-start gap-4 min-w-0">
            <div className={`w-12 h-12 flex items-center justify-center flex-shrink-0 ${isAvailable ? 'bg-[#22C55E]/10' : 'bg-[#141b4d]/10'}`}>
              <Phone weight="duotone" className={`w-6 h-6 ${isAvailable ? 'text-[#22C55E]' : 'text-[#141b4d]'}`} />
            </div>
            <div className="min-w-0">
              {isAvailable ? (
                <>
                  <p className="font-semibold text-[#09090B]" data-testid="pp-vip-headline">
                    Your {quarter_label} VIP call is available
                  </p>
                  <p className="text-sm text-[#52525B] mt-1 max-w-md">
                    Book your 30-minute strategy call with attorney Joey Vitale — included this quarter at no extra cost.
                  </p>
                </>
              ) : (
                <>
                  <p className="font-semibold text-[#09090B]" data-testid="pp-vip-headline">
                    You've used your {quarter_label} VIP call
                    <CheckCircle weight="fill" className="w-4 h-4 text-[#22C55E] inline ml-1.5 align-text-bottom" />
                  </p>
                  <p className="text-sm text-[#52525B] mt-1 max-w-md">
                    Your next included VIP call unlocks{' '}
                    <span className="font-medium text-[#09090B]">{fmtDate(next_available)}</span>.
                    Need to talk sooner? You can still book below.
                  </p>
                </>
              )}

              <div className="mt-3 space-y-1.5">
                {next_scheduled_at && (
                  <div className="flex items-center gap-2 text-sm" data-testid="pp-vip-upcoming">
                    <CalendarCheck weight="duotone" className="w-4 h-4 text-[#1d57a5] flex-shrink-0" />
                    <span className="text-[#09090B]">
                      Upcoming call scheduled for <span className="font-semibold">{fmtDateTime(next_scheduled_at)}</span>
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm" data-testid="pp-vip-last-completed">
                  <CalendarBlank weight="duotone" className="w-4 h-4 text-[#71717A] flex-shrink-0" />
                  <span className="text-[#52525B]">
                    {last_completed_at
                      ? <>Last call: <span className="font-medium text-[#09090B]">{fmtDate(last_completed_at)}</span></>
                      : "You haven't had a VIP call yet — this is a great time to book your first one."}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-shrink-0">
            <a
              href={booking_url}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center justify-center gap-2 text-sm font-semibold px-5 py-2.5 transition-colors whitespace-nowrap w-full md:w-auto ${
                isAvailable
                  ? 'bg-[#141b4d] text-white hover:bg-[#1d2461]'
                  : 'bg-white border border-[#141b4d]/30 text-[#141b4d] hover:bg-[#141b4d]/5'
              }`}
              data-testid="pp-vip-book-btn"
            >
              {next_scheduled_at ? 'Book or reschedule' : isAvailable ? 'Book my VIP call' : 'Book a call anyway'}
              <ArrowSquareOut weight="bold" className="w-4 h-4" />
            </a>
          </div>
        </div>

        {history.length > 0 && (
          <div className="mt-5 pt-4 border-t border-[#E4E4E7]" data-testid="pp-vip-history">
            <p className="label-uppercase text-[10px] text-[#71717A] font-semibold mb-2">Call history</p>
            <ul className="space-y-1.5">
              {history.map((h, i) => (
                <li key={`${h.start}-${i}`} className="flex items-center gap-2.5 text-sm" data-testid={`pp-vip-history-item-${i}`}>
                  {h.status === 'completed' ? (
                    <CheckCircle weight="fill" className="w-4 h-4 text-[#22C55E] flex-shrink-0" />
                  ) : (
                    <Clock weight="duotone" className="w-4 h-4 text-[#1d57a5] flex-shrink-0" />
                  )}
                  <span className="text-[#09090B]">{fmtDateTime(h.start)}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    h.status === 'completed'
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-sky-100 text-sky-700'
                  }`}>
                    {h.status === 'completed' ? 'Completed' : 'Upcoming'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Maintenance deadlines
// -----------------------------------------------------------------------------
function MaintenanceDeadlines({ deadlines }) {
  return (
    <div data-testid="pp-deadlines-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-[#09090B]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Upcoming Maintenance Deadlines
          {deadlines.length > 0 && (
            <span className="ml-2 text-sm font-normal text-[#71717A]">({deadlines.length})</span>
          )}
        </h3>
        {deadlines.length > 0 && (
          <p className="text-xs text-[#71717A] hidden sm:block">Sorted by urgency</p>
        )}
      </div>

      {deadlines.length === 0 ? (
        <div className="card-swiss text-center py-10" data-testid="pp-deadlines-empty">
          <div className="w-12 h-12 bg-emerald-50 flex items-center justify-center mx-auto mb-3">
            <CheckCircle weight="duotone" className="w-7 h-7 text-emerald-500" />
          </div>
          <p className="font-medium text-[#09090B]">No maintenance deadlines on the horizon.</p>
          <p className="text-sm text-[#71717A] mt-1">We'll surface §8 declarations and 10-year renewals here as they approach.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {deadlines.map((d, idx) => {
            const bucket = maintenanceUrgency(d);
            const styles = urgencyStyles[bucket];
            const stage = d.stage || 'upcoming';
            const milestoneState = (key) => {
              if (stage === 'upcoming') {
                if (key === 'opens') return 'active';
                return 'future';
              }
              if (stage === 'open') {
                if (key === 'opens') return 'before';
                if (key === 'closes') return 'active';
                return 'future';
              }
              if (stage === 'grace') {
                if (key === 'opens' || key === 'closes') return 'before';
                return 'active';
              }
              return key === 'grace' ? 'active' : 'before';
            };
            const renderMilestoneIcon = (state) => {
              if (state === 'before') {
                return <CheckCircle weight="fill" className="w-4 h-4 text-emerald-500 flex-shrink-0" />;
              }
              if (state === 'active') {
                return (
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <div className="w-3 h-3 rounded-full bg-[#141b4d] ring-4 ring-[#141b4d]/15" />
                  </div>
                );
              }
              return <Circle weight="regular" className="w-4 h-4 text-[#a1a1aa] flex-shrink-0" />;
            };

            const milestoneRow = (key, label, date, hint) => {
              const state = milestoneState(key);
              const dateTone =
                state === 'before' ? 'text-[#71717A]' :
                state === 'active' ? 'text-[#141b4d] font-semibold' :
                'text-[#52525B]';
              const labelTone =
                state === 'before' ? 'text-[#71717A] line-through decoration-[#a1a1aa]/40' :
                state === 'active' ? 'text-[#09090B] font-medium' :
                'text-[#3f3f46]';
              return (
                <div className="flex items-center gap-2.5 text-sm">
                  {renderMilestoneIcon(state)}
                  <span className={`flex-1 ${labelTone}`}>{label}{hint && <span className="text-[#a1a1aa] font-normal"> {hint}</span>}</span>
                  <span className={`tabular-nums ${dateTone}`}>{fmtDate(date)}</span>
                </div>
              );
            };

            const dMark = d.mark_text || d.trademark_name;
            const mailtoStartFiling = `mailto:clients@indielaw.com?subject=${encodeURIComponent(`Start ${d.label} filing — ${dMark}`)}&body=${encodeURIComponent(`Hi Indie Law team,\n\nI'd like to get started on my upcoming ${d.label} filing for ${dMark}${d.registration_number ? ` (Reg #${d.registration_number})` : ''}.\n\nFiling window opens: ${d.window_opens}\nStandard deadline: ${d.deadline_closes}\nGrace ends: ${d.grace_ends}\n\nLet me know what you need from me to get this moving.\n\nThanks!\n`)}`;

            return (
              <div
                key={`${d.matter_id}-${d.label}-${idx}`}
                className={`card-swiss block ${styles.card}`}
                data-testid={`pp-deadline-${d.matter_id}-${idx}`}
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="font-semibold text-[#09090B] truncate">{d.mark_text || d.trademark_name}</p>
                      {d.registration_number && (
                        <span className="text-xs text-[#71717A]">Reg #{d.registration_number}</span>
                      )}
                    </div>
                    <p className="text-sm text-[#09090B] font-medium">{d.label}</p>
                    {d.description && (
                      <p className="text-xs text-[#71717A] mt-1 max-w-2xl">{d.description}</p>
                    )}
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${styles.chip}`}>
                      {d.actionable_label} {countdownLabel(d.days_to_actionable)}
                    </span>
                  </div>
                </div>

                <div className="space-y-1.5 mt-3 pt-3 border-t border-[#E4E4E7]">
                  {milestoneRow('opens', 'Filing window opens', d.window_opens, '(earliest date to file)')}
                  {milestoneRow('closes', 'Standard deadline', d.deadline_closes, '(last day at standard fee)')}
                  {milestoneRow('grace', 'Grace period ends', d.grace_ends, '(+extra fee)')}
                </div>

                <div className="mt-4 pt-3 border-t border-[#E4E4E7] flex flex-wrap items-center gap-3 justify-between">
                  <p className="text-xs text-[#52525B] max-w-md">
                    Government filing fees apply per class of goods/services.{' '}
                    <a
                      href="https://www.uspto.gov/learning-and-resources/fees-and-payment/uspto-fee-schedule#TM%20Post%20registration"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#1d57a5] hover:underline"
                    >
                      View USPTO post-registration fees
                    </a>
                    .
                  </p>
                  <a
                    href={mailtoStartFiling}
                    className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-1.5 bg-[#141b4d] text-white hover:bg-[#1d2461] transition-colors whitespace-nowrap w-full sm:w-auto"
                    data-testid={`pp-deadline-start-${d.matter_id}-${idx}`}
                  >
                    Email us to get started
                    <ArrowSquareOut weight="bold" className="w-3 h-3" />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {deadlines.length > 0 && (
        <p className="text-xs text-[#71717A] mt-4 leading-relaxed" data-testid="pp-aor-footnote">
          <Info weight="duotone" className="w-3.5 h-3.5 inline mr-1 align-text-bottom" />
          Only trademarks where Indie Law is listed as your attorney of record at the USPTO will appear here.
          Have other registrations you'd like us to track?{' '}
          <a
            href={`mailto:clients@indielaw.com?subject=${encodeURIComponent('Add another trademark to my Protection Plan')}`}
            className="text-[#1d57a5] hover:underline"
          >
            Let us know
          </a>
          .
        </p>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Plan benefits (CTAs)
// -----------------------------------------------------------------------------
function PlanBenefits({ drive_folder_url }) {
  const mailtoSearch = `mailto:clients@indielaw.com?subject=${encodeURIComponent('Request My Free Quarterly Trademark Search')}&body=${encodeURIComponent("Hi Indie Law team,\n\nI'd like to use my free quarterly trademark search. The mark / phrase I'd like searched is:\n\n—\n\nGoods/services I plan to use it for:\n\n—\n")}`;
  const mailtoUpdate = `mailto:clients@indielaw.com?subject=${encodeURIComponent('Update My Trademark Details')}&body=${encodeURIComponent("Hi Indie Law team,\n\nI need to update the details on file with the USPTO for my registered trademark(s). Here's what changed:\n\n— Type of change (business address, LLC name, ownership, etc.):\n— New information:\n— Effective date:\n\nThanks!\n")}`;

  return (
    <div data-testid="pp-resources-section">
      <h3 className="text-lg font-semibold text-[#09090B] mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
        Use Your Plan Benefits
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <a
          href={PROTECTION_PLAN_CALL_BOOKING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="card-swiss group hover:shadow-md transition-all bg-[#141b4d]/[0.04] border-[#141b4d]/15 hover:bg-[#141b4d]/[0.08]"
          data-testid="pp-cta-vip-call"
        >
          <div className="w-10 h-10 bg-[#141b4d]/10 flex items-center justify-center mb-3 group-hover:bg-[#141b4d]/20 transition-colors">
            <Phone weight="duotone" className="w-5 h-5 text-[#141b4d]" />
          </div>
          <p className="font-semibold text-[#09090B]">Schedule Your VIP Call</p>
          <p className="text-sm text-[#52525B] mt-1">
            30-minute strategy call with attorney Joey Vitale — included quarterly.
          </p>
          <p className="text-xs font-medium text-[#141b4d] mt-3 inline-flex items-center gap-1">
            Book a time
            <ArrowSquareOut weight="bold" className="w-3 h-3" />
          </p>
        </a>

        <a
          href={mailtoSearch}
          className="card-swiss group hover:shadow-md transition-all bg-[#FFD60A]/10 border-[#FFD60A]/40 hover:bg-[#FFD60A]/20"
          data-testid="pp-cta-free-search"
        >
          <div className="w-10 h-10 bg-[#FFD60A]/30 flex items-center justify-center mb-3 group-hover:bg-[#FFD60A]/50 transition-colors">
            <Star weight="duotone" className="w-5 h-5 text-[#a17900]" />
          </div>
          <p className="font-semibold text-[#09090B]">Free Trademark Search</p>
          <p className="text-sm text-[#52525B] mt-1">
            Thinking about a new name or product line? One free knock-out search per quarter.
          </p>
          <p className="text-xs font-medium text-[#a17900] mt-3 inline-flex items-center gap-1">
            Request a search
            <ArrowSquareOut weight="bold" className="w-3 h-3" />
          </p>
        </a>

        <a
          href={mailtoUpdate}
          className="card-swiss group hover:shadow-md transition-all bg-emerald-50/60 border-emerald-200/60 hover:bg-emerald-100/60"
          data-testid="pp-cta-update-details"
        >
          <div className="w-10 h-10 bg-emerald-100 flex items-center justify-center mb-3 group-hover:bg-emerald-200 transition-colors">
            <Buildings weight="duotone" className="w-5 h-5 text-emerald-700" />
          </div>
          <p className="font-semibold text-[#09090B]">Update Trademark Details</p>
          <p className="text-sm text-[#52525B] mt-1">
            Moved your business, changed your LLC name, or new ownership? We'll file the USPTO update for you.
          </p>
          <p className="text-xs font-medium text-emerald-700 mt-3 inline-flex items-center gap-1">
            Tell us what changed
            <ArrowSquareOut weight="bold" className="w-3 h-3" />
          </p>
        </a>

        {drive_folder_url ? (
          <a
            href={drive_folder_url}
            target="_blank"
            rel="noopener noreferrer"
            className="card-swiss group hover:shadow-md transition-all bg-sky-50/60 border-sky-200/60 hover:bg-sky-100/60"
            data-testid="pp-cta-documents"
          >
            <div className="w-10 h-10 bg-sky-100 flex items-center justify-center mb-3 group-hover:bg-sky-200 transition-colors">
              <FolderOpen weight="duotone" className="w-5 h-5 text-sky-700" />
            </div>
            <p className="font-semibold text-[#09090B]">Your Documents</p>
            <p className="text-sm text-[#52525B] mt-1">
              Registration certificates, USPTO filings, and any reports we share with you.
            </p>
            <p className="text-xs font-medium text-sky-700 mt-3 inline-flex items-center gap-1">
              Open Google Drive
              <ArrowSquareOut weight="bold" className="w-3 h-3" />
            </p>
          </a>
        ) : (
          <div className="card-swiss opacity-70 bg-sky-50/30 border-sky-200/40" data-testid="pp-cta-documents-empty">
            <div className="w-10 h-10 bg-sky-100/60 flex items-center justify-center mb-3">
              <FolderOpen weight="duotone" className="w-5 h-5 text-sky-600" />
            </div>
            <p className="font-semibold text-[#09090B]">Your Documents</p>
            <p className="text-sm text-[#52525B] mt-1">
              Your secure document folder will appear here once it's set up.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Learn more (collapsed by default): renewal timeline, filing windows, §8/§9/§15, scams
// -----------------------------------------------------------------------------
function LearnMore() {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="pp-learn-more">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border bg-white hover:bg-[#faf7f0] transition-colors cursor-pointer text-left"
        style={{ borderColor: '#e5dcc8' }}
        data-testid="pp-learn-more-toggle"
      >
        <CaretRight
          weight="bold"
          className="w-4 h-4 flex-shrink-0 transition-transform duration-200"
          style={{ color: '#9a9488', transform: open ? 'rotate(90deg)' : 'none' }}
        />
        <span className="font-semibold text-sm flex-1" style={{ color: '#141b4d' }}>
          Learn more about keeping your registered trademark(s) alive
        </span>
        <span className="text-xs font-medium" style={{ color: '#9a9488' }}>{open ? 'Hide' : 'Show'}</span>
      </button>

      {open && (
        <div className="mt-4" data-testid="pp-learn-more-content">
          {/* Scam warning */}
          <div className="card-swiss border-l-4 border-l-amber-500 bg-amber-50/40 mb-4" data-testid="pp-scam-warning">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Warning weight="duotone" className="w-5 h-5 text-amber-700" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-[#09090B]">Watch out for trademark scams.</p>
                <p className="text-sm text-[#3f3f46] mt-1 leading-relaxed">
                  Once your mark is registered, your address becomes public — and you'll almost certainly start getting
                  official-looking mail, email, and invoices from third parties claiming your trademark "needs renewal"
                  or is "about to expire," often demanding hundreds or thousands of dollars. Most of it is a scam.
                </p>
                <p className="text-sm text-[#09090B] mt-2 leading-relaxed">
                  <strong>The Indie Law team is your point of contact with the USPTO.</strong> If you ever get
                  unexpected mail or email about your trademark, forward it to{' '}
                  <a href="mailto:clients@indielaw.com" className="text-[#1d57a5] hover:underline">clients@indielaw.com</a>{' '}
                  before paying or responding to anything.
                </p>
                <a
                  href="https://www.uspto.gov/trademarks/protect/recognizing-common-scams"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-medium text-[#1d57a5] hover:underline mt-3"
                  data-testid="pp-scam-uspto-link"
                >
                  Read the USPTO's guide to common trademark scams
                  <ArrowSquareOut weight="bold" className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>

          {/* Milestone timeline */}
          <div className="card-swiss mb-4 bg-[#141b4d]/[0.03] border-[#141b4d]/15">
            <p className="font-semibold text-[#141b4d] mb-1">When renewals are due</p>
            <p className="text-sm text-[#52525B] mb-4">
              Once registered, the USPTO requires periodic filings to keep your trademark alive:
            </p>
            <ol className="space-y-3">
              {[
                {
                  year: 'Years 5–6',
                  title: 'First Declaration of Use',
                  detail: 'Confirms you\'re still using the mark on the goods or services in your registration. Files as §8.',
                },
                {
                  year: 'Years 9–10',
                  title: 'Combined Declaration + Renewal',
                  detail: 'Same use declaration (§8) plus an official 10-year renewal (§9), filed together.',
                },
                {
                  year: 'Every 10 years after',
                  title: 'Recurring §8 & §9',
                  detail: 'Repeat the combined filing every decade to keep the registration in force.',
                },
              ].map((m, i) => (
                <li key={m.year} className="flex gap-4 items-start">
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#FFD60A]/20 flex items-center justify-center text-xs font-bold text-[#09090B]">
                    {i + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="label-uppercase text-[10px] text-[#1d57a5] font-semibold">{m.year}</span>
                      <span className="font-medium text-[#09090B]">{m.title}</span>
                    </div>
                    <p className="text-sm text-[#52525B] mt-0.5">{m.detail}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* Filing windows */}
          <div className="card-swiss mb-4 bg-emerald-50/40 border-emerald-200/60">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Calendar weight="duotone" className="w-5 h-5 text-emerald-700" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-[#09090B]">You have more time than you might think.</p>
                <ul className="text-sm text-[#3f3f46] mt-2 space-y-2 leading-relaxed">
                  <li>
                    <strong className="text-[#09090B]">12-month filing window.</strong>{' '}
                    Each milestone has a full year in which the filing can be submitted on time (for example, anywhere
                    in the 12 months between years 5 and 6).
                  </li>
                  <li>
                    <strong className="text-[#09090B]">6-month grace period.</strong>{' '}
                    If the window closes, the USPTO gives you another 6 months to file with an additional fee. Past
                    that, the registration is cancelled and can't be revived — you'd have to apply from scratch.
                  </li>
                  <li>
                    <strong className="text-[#09090B]">We watch the clock for you.</strong>{' '}
                    We'll reach out well before each window opens so nothing slips through the cracks.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* §8 vs §9 vs §15 */}
          <div className="card-swiss bg-sky-50/40 border-sky-200/60">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-9 h-9 bg-sky-100 flex items-center justify-center flex-shrink-0">
                <Info weight="duotone" className="w-5 h-5 text-sky-700" />
              </div>
              <div>
                <p className="font-semibold text-[#09090B]">§8 vs §9 vs §15 — what do these mean?</p>
                <p className="text-sm text-[#52525B] mt-1">
                  Three different filings show up in trademark maintenance. Here's the short version:
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
              <div className="border border-sky-200 bg-white p-4">
                <p className="label-uppercase text-[10px] text-[#1d57a5] font-semibold mb-1">Section 8</p>
                <p className="font-medium text-[#09090B] text-sm">Declaration of Use</p>
                <p className="text-xs text-[#52525B] mt-2 leading-relaxed">
                  A sworn statement (with proof) that you're still using the mark on the goods or services listed in
                  your registration. Required at years 5–6 and again at 9–10.
                </p>
              </div>
              <div className="border border-sky-200 bg-white p-4">
                <p className="label-uppercase text-[10px] text-[#1d57a5] font-semibold mb-1">Section 9</p>
                <p className="font-medium text-[#09090B] text-sm">Renewal</p>
                <p className="text-xs text-[#52525B] mt-2 leading-relaxed">
                  The actual 10-year renewal that extends your registration. Filed jointly with §8 at years 9–10 and
                  every 10 years after that.
                </p>
              </div>
              <div className="border border-[#FFD60A]/50 bg-[#FFD60A]/10 p-4">
                <p className="label-uppercase text-[10px] text-[#a17900] font-semibold mb-1">Section 15</p>
                <p className="font-medium text-[#09090B] text-sm">Incontestability <span className="text-[#a17900] font-normal">· Optional</span></p>
                <p className="text-xs text-[#52525B] mt-2 leading-relaxed">
                  After 5 continuous years of use, you can declare your mark "incontestable" — which closes off most
                  grounds someone could use to challenge it later. <strong className="text-[#09090B]">We recommend
                  filing §15 together with your first §8</strong> to lock in the strongest possible protection in one step.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Active view
// -----------------------------------------------------------------------------
function ProtectionPlanActive({ data }) {
  const { deadlines = [], drive_folder_url } = data;

  return (
    <div className="space-y-10" data-testid="protection-plan-active">
      {/* 0. Plan at a glance (merged status strip) */}
      <PlanGlanceStrip data={data} />

      {/* 1. This quarter's monitoring results */}
      <QuarterlyMonitoring cadence={data.brand_watch} />

      {/* 2. Upcoming maintenance deadlines */}
      <MaintenanceDeadlines deadlines={deadlines} />

      {/* 3. Your VIP strategy call */}
      <VipCallSection initial={data.vip_call} />

      {/* 4. Use your plan benefits */}
      <PlanBenefits drive_folder_url={drive_folder_url} />

      {/* 5. Learn more (collapsed by default) */}
      <LearnMore />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Promo view (unchanged for non-PP clients)
// -----------------------------------------------------------------------------
function ProtectionPlanPromo({ registeredCount = 0 }) {
  const hasRegistered = registeredCount > 0;
  return (
    <div className="space-y-8" data-testid="protection-plan-promo">
      <div className="card-swiss text-center py-12">
        <div className="w-20 h-20 bg-[#FFD60A]/10 flex items-center justify-center mx-auto mb-6">
          <ShieldCheck weight="duotone" className="w-12 h-12 text-[#FFD60A]" />
        </div>
        <h1 className="text-3xl font-bold text-[#09090B] tracking-tight mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          {hasRegistered
            ? <>Your trademark is registered.<br />Now keep it protected.</>
            : <>Protect your brand<br />for the long haul.</>}
        </h1>
        <p className="text-[#52525B] max-w-xl mx-auto mb-8">
          {hasRegistered
            ? "Ongoing monitoring helps catch copycats, missed deadlines, and enforcement issues before they become expensive. We keep an eye on things so you don't have to."
            : "Once your trademark registers, ongoing monitoring helps catch copycats, missed deadlines, and enforcement issues before they become expensive. Here's how the Protection Plan keeps your brand safe long-term."}
        </p>
        <a
          href="https://indielaw.thrivecart.com/protection-plan-monitoring/"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary inline-flex items-center justify-center gap-2 w-full sm:w-auto text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4"
          data-testid="protection-plan-signup-btn"
        >
          Protect My Trademark
          <ArrowSquareOut weight="bold" className="w-5 h-5" />
        </a>
        <p className="text-sm text-[#A1A1AA] mt-3">$1 a day. Cancel anytime.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card-swiss">
          <h3 className="font-semibold text-[#09090B] mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Without Monitoring
          </h3>
          <ul className="space-y-3">
            {[
              'Similar filings may go unnoticed until damage is done',
              'Higher legal costs to fix issues',
              'Increased risk of rebranding',
              'Missed maintenance deadlines'
            ].map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm text-[#52525B]">
                <span className="text-red-400 mt-0.5">&#x2715;</span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="card-swiss border-[#22C55E]/30 bg-[#22C55E]/5">
          <h3 className="font-semibold text-[#09090B] mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            With Protection Plan
          </h3>
          <ul className="space-y-3">
            {[
              'Potential issues caught early',
              'Clear, timely guidance on next steps',
              'Lower-cost enforcement options',
              'Deadlines tracked and handled',
              'Less risk to your brand'
            ].map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm text-[#09090B]">
                <CheckCircle weight="fill" className="w-4 h-4 text-[#22C55E] flex-shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-[#09090B] mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          What's Included
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            'Monthly trademark monitoring',
            'Quarterly infringement reports',
            'Quarterly VIP call with attorney Joey Vitale',
            'One free trademark search every quarter',
            'Trademark maintenance reminders',
            'Assistance with required maintenance filings'
          ].map((item, i) => (
            <motion.div
              key={item}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="card-swiss flex items-start gap-3"
            >
              <CheckCircle weight="fill" className="w-5 h-5 text-[#FFD60A] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#09090B]">{item}</p>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="text-center py-6">
        <h3 className="text-xl font-bold text-[#09090B] mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Protect the trademark you've already invested in.
        </h3>
        <p className="text-[#52525B] mb-6">
          Ongoing trademark monitoring to help catch issues early and avoid costly problems.
        </p>
        <a
          href="https://indielaw.thrivecart.com/protection-plan-monitoring/"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary inline-flex items-center justify-center gap-2 w-full sm:w-auto"
          data-testid="protection-plan-signup-btn-bottom"
        >
          Protect My Trademark
          <ArrowSquareOut weight="bold" className="w-5 h-5" />
        </a>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Page wrapper
// -----------------------------------------------------------------------------
export default function ProtectionPlanPage() {
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlanStatus();
  }, []);

  const fetchPlanStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/client/protection-plan`);
      setPlanData(response.data);
    } catch (error) {
      toast.error('Failed to load protection plan info');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#FFD60A] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="protection-plan-page">
        <div className="mb-6">
          <p className="label-uppercase text-[#52525B] mb-1">Trademark Security</p>
          <h1 className="text-3xl font-bold text-[#09090B] tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Protection Plan
          </h1>
        </div>

        {planData?.has_protection_plan ? (
          <ProtectionPlanActive data={planData} />
        ) : (
          <ProtectionPlanPromo registeredCount={planData?.registered_count || 0} />
        )}
      </div>
    </DashboardLayout>
  );
}
